import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import type { SearchResult, SourceDiscoverResult, SourceManifest } from "../api";
import { DiscoverSection } from "./source-discover-items";
import { DiscoverCategoryTabs, DiscoverPagination } from "./source-discover-nav";
import { isHomeDiscover, sectionSupportsPagination } from "./source-discover-utils";

export function SourceDiscoverPanel({
  source,
  discoverCapable,
  loading,
  discover,
  homeDiscover,
  onNavigate,
  onHomeSelect,
  onBookSelect,
}: {
  source: SourceManifest;
  discoverCapable: boolean;
  loading: boolean;
  discover?: SourceDiscoverResult;
  homeDiscover?: SourceDiscoverResult;
  onNavigate: (targetUrl: string) => void;
  onHomeSelect: () => void;
  onBookSelect: (result: SearchResult) => void;
}) {
  if (!discoverCapable) {
    return <div className="border-t px-4 py-6 text-sm text-muted-foreground">该书源没有提供发现内容</div>;
  }

  if (loading && !discover) {
    return (
      <div className="flex items-center gap-2 border-t px-4 py-6 text-sm text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" />
        正在加载书源发现内容
      </div>
    );
  }

  if (!discover?.sections.length) {
    return <div className="border-t px-4 py-6 text-sm text-muted-foreground">暂无发现内容</div>;
  }

  return (
    <div className="border-t bg-muted/20 px-4 py-4">
      <div className="space-y-5">
        <DiscoverSections
          source={source}
          discover={discover}
          homeDiscover={homeDiscover}
          loading={loading}
          onNavigate={onNavigate}
          onHomeSelect={onHomeSelect}
          onBookSelect={onBookSelect}
        />
      </div>
    </div>
  );
}

function DiscoverSections({
  source,
  discover,
  homeDiscover,
  loading,
  onNavigate,
  onHomeSelect,
  onBookSelect,
}: {
  source: SourceManifest;
  discover: SourceDiscoverResult;
  homeDiscover?: SourceDiscoverResult;
  loading: boolean;
  onNavigate: (targetUrl: string) => void;
  onHomeSelect: () => void;
  onBookSelect: (result: SearchResult) => void;
}) {
  const sections = discover.sections;
  const insideHome = isHomeDiscover(discover);
  const navSections = sections.filter(section => section.layout === "nav");
  const contentSections = sections.filter(section => section.layout !== "nav");
  const [activeSectionId, setActiveSectionId] = useState(contentSections[0]?.id ?? "");
  const activeSection = contentSections.find(section => section.id === activeSectionId) ?? contentSections[0];
  const showContentTabs = contentSections.length > 1;

  useEffect(() => {
    if (!contentSections.length) return;
    if (!contentSections.some(section => section.id === activeSectionId)) setActiveSectionId(contentSections[0]!.id);
  }, [activeSectionId, contentSections]);

  return (
    <div className="space-y-4">
      {navSections.map(section => (
        <DiscoverCategoryTabs
          key={section.id}
          section={section}
          insideHome={insideHome}
          currentUrl={discover.currentUrl}
          loading={loading}
          homeDiscover={homeDiscover}
          onNavigate={onNavigate}
          onHomeSelect={onHomeSelect}
        />
      ))}
      {contentSections.length > 0 && (
        <section className="space-y-3">
          {showContentTabs && (
            <div className="flex gap-2 overflow-x-auto rounded-full bg-background p-1">
              {contentSections.map(section => (
                <button
                  key={section.id}
                  type="button"
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors ${
                    section.id === activeSection?.id ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  {section.title}
                </button>
              ))}
            </div>
          )}
          {activeSection && (
            <DiscoverSection source={source} section={activeSection} showTitle={false} onNavigate={onNavigate} onBookSelect={onBookSelect} />
          )}
          {sectionSupportsPagination(activeSection) && (
            <DiscoverPagination discover={discover} loading={loading} onNavigate={onNavigate} />
          )}
        </section>
      )}
    </div>
  );
}
